import React, { lazy, Suspense } from 'react'

import { Redirect, Route, RouteProps, Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'

import authService from '../services/auth'
import { ScopesUtil } from '../store/application/utils/scopes.util'
import { AUTH_ROUTES } from './auth'
import Loading from '../components/layout/loading'
import { VerifyUserType } from '../components/verify.user.type'

export enum LogicalStrategy {
    OR = 'or',
    AND = 'and'
}

interface IPrivateRouteProps extends RouteProps {
    key?: number
    component?: any
    private?: boolean
    redirect?: string
    path?: string
    routes?: any
    properties?: any[]
    scopes?: string[]
    userTypes?: string[]
    logicalStrategy?: LogicalStrategy
}

export const RouteWithSubRoutes = (route: IPrivateRouteProps) => {
    return (
        <Route
            path={route.path}
            exact={route.exact}
            strict={route.strict}
            render={(props: RouteProps) => {
                /* Verify user is authenticated */
                if (route.private && !authService.isAuthenticated()) {
                    return (
                        <Redirect
                            to={{ pathname: '/login', state: { from: props.location } }}
                        />
                    )
                }
                /* Make sure the user has the necessary scopes */
                if (route.scopes) {
                    try {
                        if (!ScopesUtil.verifyScopes(route.scopes, route.logicalStrategy)) {
                            throw new Error('ACCESS DENIED')
                        }
                    } catch (e) {
                        return (
                            <Redirect
                                to={{
                                    pathname: '/access_denied',
                                    state: { from: props.location }
                                }}
                            />
                        )
                    }
                }
                /* Make sure the user has the necessary type */
                if (route?.userTypes) {
                    try {
                        if (!VerifyUserType.validate(route?.userTypes)) {
                            throw new Error('ACCESS DENIED')
                        }
                    } catch (e) {
                        return (
                            <Redirect
                                to={{
                                    pathname: '/access_denied',
                                    state: { from: props.location }
                                }}
                            />
                        )
                    }
                }
                /* Checks for a redirect link */
                if (route.redirect) {
                    return (
                        <Redirect
                            to={{
                                pathname: `${route.redirect}`,
                                state: { from: props.location }
                            }}
                        />
                    )
                }
                return (
                    <route.component
                        {...props}
                        {...route.properties}
                        exact={true}
                        routes={route.routes}
                    />
                )
            }}
        />
    )
}

const Layout = lazy(() => import('../containers/layout/layout'))
const HomePage = lazy(() => import('../containers/home/home'))
const NotFound = lazy(() => import('../components/layout/escape.pages/not.found'))
const AccessDenied = lazy(() => import('../components/layout/escape.pages/access.denied'))
const InternalError = lazy(() => import('../components/layout/escape.pages/internal.error'))
const NewRegister = lazy(() => import('../containers/home/personal.data'))

const FollowUp = lazy(() => import('../containers/home/patient.information'))
const NewFollowUp = lazy(() => import('../containers/home/register.followUp'))
const ProfileHP = lazy(() => import('../components/profile/health.professional'))
const ProfileNV = lazy(() => import('../components/profile/navigator'))
const ProfileAdmin = lazy(() => import('../components/profile/admin'))
// const Profile = lazy(() => import('../containers/user/profile'))
const Alert = lazy(() => import('../containers/alert/list'))
const Report = lazy(() => import('../containers/report'))
const Administration = lazy(() => import('../containers/administration/list'))
const Map = lazy(() => import('../containers/relevance/map'))
const About = lazy(() => import('../containers/about'))
const Manager = lazy(() => import('../containers/active.search'))
// const ActiveSearch = lazy(() => import('../containers/active.search'))

const ROUTES: IPrivateRouteProps | any = [
    { path: '/', exact: true, redirect: '/app/home' },
    ...AUTH_ROUTES,
    {
        path: '/app',
        strict: true,
        private: true,
        component: Layout,
        routes: [
            { path: '/app', exact: true, redirect: '/app/home' },
            {
                path: '/app/home',
                exact: true,
                component: HomePage
            },
            {
                path: '/app/home/register',
                exact: true,
                component: NewRegister
            },
            {
                path: '/app/home/register/:patientId',
                exact: true,
                component: NewRegister
            }
            ,
            {
                path: '/app/home/:patientId',
                exact: true,
                component: FollowUp
            },
            {
                path: '/app/home/:patientId/followups',
                exact: true,
                component: FollowUp
            },
            {
                path: '/app/home/:patientId/followups/:followUpId',
                exact: true,
                component: NewFollowUp
            },
            {
                path: '/app/administration',
                exact: true,
                component: Administration
            },
            {
                path: '/app/navigators',
                exact: true,
                component: Administration
            },
            {
                path: '/app/healthprofessionals',
                exact: true,
                component: Administration
            },
            {
                path: '/app/navigators/:navigatorId',
                exact: true,
                component: ProfileNV
            },
            {
                path: '/app/healthprofessionals/:healthprofessionalId',
                exact: true,
                component: ProfileHP
            },
            {
                path: '/app/administration/register_ph',
                exact: true,
                component: ProfileHP
            },
            {
                path: '/app/administration/register_nv',
                exact: true,
                component: ProfileNV
            },
            {
                path: '/app/admins',
                exact: true,
                component: ProfileAdmin
            },
            {
                path: '/app/admins/:adminId',
                exact: true,
                component: ProfileAdmin
            },
            {
                path: '/app/alert',
                exact: true,
                component: Alert
            },
            {
                path: '/app/relevance',
                exact: true,
                component: Map
            },
            {
                path: '/app/report',
                exact: true,
                component: Report
            },
            {
                path: '/app/manager',
                exact: true,
                component: Manager
            },

            { path: '*', redirect: '/not_found' }
        ]
    },
    {
        path: '/not_found',
        component: NotFound,
        exact: true
    },
    {
        path: '/access_denied',
        component: AccessDenied,
        exact: true
    },
    {
        path: '/internal_error',
        component: InternalError,
        exact: true
    },
    {
        path: '/about',
        component: About,
        exact: true
    },
    {
        path: '*',
        component: NotFound
    }
]

class Routes extends React.Component<{ history: any }> {
    public render() {
        return (
            <ConnectedRouter history={this.props.history}>
                <Suspense fallback={<Loading />}>
                    <Switch>
                        {
                            ROUTES
                                .map((route:IPrivateRouteProps, i:number) => (
                                    <RouteWithSubRoutes key={i} {...route} />
                                ))
                        }
                    </Switch>
                </Suspense>
            </ConnectedRouter>
        )
    }
}

export default Routes
